/** THE CRM RUNS ON EASTERN TIME. (Tom, 2026-07-28: "the crm and everything
 * should be based on my time which is EST".)
 *
 * The business is in Florida, and so is every date that matters: closing
 * dates, post-occupancy end dates, task due dates, quiet hours, the morning
 * digest. The server side already agrees — notif_scheduler.py, digest.py and
 * run_jobs.py all use ZoneInfo("America/New_York"). The browser did not: it
 * used whatever timezone the device was set to. On Tom's Mac that happens to
 * be the same thing, so it looked correct; on a teammate's laptop in another
 * state, or a phone that picked up a different zone while travelling, "3 days
 * until closing" quietly became 2 or 4.
 *
 * NOTE ON "EST": America/New_York, not a fixed -5. Literal EST is UTC-5 all
 * year, so hardcoding it would put the whole app an hour off from March to
 * November — including right now. America/New_York follows EST in winter and
 * EDT in summer, which is what "my time" actually means.
 */
export const APP_TZ = "America/New_York";

const YMD = new Intl.DateTimeFormat("en-US", {
  timeZone: APP_TZ, year: "numeric", month: "2-digit", day: "2-digit",
});

/** Today's calendar date in Eastern, as a LOCAL-midnight Date.
 *
 * Local midnight, not Eastern midnight, on purpose: it is only ever compared
 * against dates built the same way (parseDealDate), so both sides share a
 * frame and the subtraction gives whole days. Building a real Eastern-midnight
 * instant would reintroduce the offset this exists to remove. */
export function todayAppTz(now: Date = new Date()): Date {
  const parts = YMD.formatToParts(now);
  const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value);
  return new Date(get("year"), get("month") - 1, get("day"));
}

/** Today's date in Eastern as "YYYY-MM-DD", for <input type="date"> defaults
 * and for stamping a business date.
 *
 * Use this instead of `new Date().toISOString().slice(0, 10)`, which is the
 * UTC date — and UTC rolls over at 8pm EDT / 7pm EST. Logging an expense at
 * 9pm on Jul 31 pre-filled the date field with Aug 1, stored it, displayed it,
 * and pushed it to QuickBooks in the wrong month. */
export function todayIsoAppTz(now: Date = new Date()): string {
  const parts = YMD.formatToParts(now);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Whole days from today-in-Eastern to a local-midnight target date.
 * Rounded, so a 23- or 25-hour DST day can't shift a countdown by one. */
export function daysFromTodayAppTz(target: Date, now: Date = new Date()): number {
  return Math.round((target.getTime() - todayAppTz(now).getTime()) / 86400000);
}

/** Wall-clock date/time in Eastern regardless of the device's timezone —
 * for stamps like "Jul 28, 3:20 PM" that must read the same for everyone. */
export function fmtAppTz(ms: number, opts: Intl.DateTimeFormatOptions): string {
  if (!ms) return "";
  return new Date(ms).toLocaleString("en-US", { timeZone: APP_TZ, ...opts });
}
